import { useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { listOptions, useConfig } from '../context/ConfigContext';

// TS port of the source (Falah) app's pages/ProcurementForm.jsx. Creates a
// PurchaseRequest, then uploads each attached quotation file as a Document
// (category: 'quotation') via PurchaseRequestController::storeDocument() —
// only edit-time while status is 'draft'.
interface RequestFormState {
  title: string;
  item_name: string;
  request_type: string;
  description: string;
  estimated_cost: string;
  quantity: string;
  purpose: string;
}

const emptyForm: RequestFormState = {
  title: '',
  item_name: '',
  request_type: '',
  description: '',
  estimated_cost: '',
  quantity: '1',
  purpose: '',
};

type FormErrors = Record<string, string[]>;

export function ProcurementForm() {
  const { settings, lists } = useConfig();
  const navigate = useNavigate();
  const [form, setForm] = useState<RequestFormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);
  const [quotations, setQuotations] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const set =
    (key: keyof RequestFormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const pickQuotation = () => fileInputRef.current?.click();

  const onQuotationSelected = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setQuotations((q) => [...q, file]);
  };

  const removeQuotation = (index: number) => setQuotations((q) => q.filter((_, i) => i !== index));

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const res = await api.post<{ id: number }>('/purchase-requests', form);
      for (const file of quotations) {
        const data = new FormData();
        data.append('file', file);
        await api.post(`/purchase-requests/${res.data.id}/documents`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      navigate(`/procurement/requests/${res.data.id}`);
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Add request</h1>
      </header>

      <div className="card">
        <form onSubmit={save} className="form-grid">
          {errors.general && <div className="alert alert-error">{errors.general[0]}</div>}
          <label className="span-2">
            Name *
            <input value={form.title} onChange={set('title')} required />
            {errors.title && <small className="error">{errors.title[0]}</small>}
          </label>
          <label className="span-2">
            Item name *
            <input value={form.item_name} onChange={set('item_name')} required />
            {errors.item_name && <small className="error">{errors.item_name[0]}</small>}
          </label>
          <label className="span-2">
            Request type *
            <select value={form.request_type} onChange={set('request_type')} required>
              <option value="">Select…</option>
              {listOptions(lists, 'purchase_request_type').map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            {errors.request_type && <small className="error">{errors.request_type[0]}</small>}
          </label>
          <label className="span-2">
            Description
            <textarea rows={2} value={form.description} onChange={set('description')} />
          </label>
          <label>
            Estimated cost ({settings.currency}) *
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={form.estimated_cost}
              onChange={set('estimated_cost')}
              required
            />
            {errors.estimated_cost && <small className="error">{errors.estimated_cost[0]}</small>}
          </label>
          <label>
            Quantity
            <input type="number" min="1" value={form.quantity} onChange={set('quantity')} />
          </label>
          <label className="span-2">
            Purpose
            <textarea rows={2} value={form.purpose} onChange={set('purpose')} />
          </label>
          <div className="span-2">
            <h3 style={{ marginBottom: 10 }}>Quotations</h3>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>File</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {quotations.map((file, index) => (
                    <tr key={`${file.name}-${index}`}>
                      <td>{file.name}</td>
                      <td className="right">
                        <button type="button" className="btn btn-danger-ghost" onClick={() => removeQuotation(index)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                  {quotations.length === 0 && (
                    <tr>
                      <td colSpan={2} className="muted center">
                        No quotations attached yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <button type="button" className="btn btn-ghost" onClick={pickQuotation} style={{ marginTop: 10 }}>
              + Attach quotation
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/webp"
              onChange={onQuotationSelected}
              hidden
            />
          </div>
          <div className="form-actions span-2">
            <Link className="btn btn-ghost" to="/procurement/requests">
              Cancel
            </Link>
            <button className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
